import React, {useState} from 'react';
import {Button, Dialog, FormGroup, Switch} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';
import InfiniteScroll from "react-infinite-scroll-component";

import {PDFMergeOptionsForm}
  from "../../../../features/reports/ReportPageContainer/ReportForm/MergeContent";
import {useGetPdfMergeContent} from "../hooks";
import FilenameInput from './FilenameInput';

interface MergePdfOption {
  position?: number;
  include?: boolean;
  uid?: string;
}

interface MergePdfConfig {
  enabled: boolean;
  filename: string;
  options: MergePdfOption[];
}

interface MergePdfProps {
  config: MergePdfConfig;
  onConfigChange?: (event: any) => void;
  attachments: string[];
  disabled?: boolean;
}

const DEFAULT_MERGE_PDF_CONFIG: MergePdfConfig = {
  enabled: false,
  filename: '',
  options: [],
}

export const MergePdf = ({
  config = DEFAULT_MERGE_PDF_CONFIG,
  onConfigChange,
  attachments,
  disabled = false,
}: MergePdfProps) => {
  const [pdfFilename, setPdfFilename] = useState(config.filename);
  const [isPDFDialogOpen, setIsPDFDialogOpen] = useState(false);

  // custom hook to get PDF merge content from Report blocks
  const {
    content: pdfMergeContent,
    mergePdfConfig,
    setMergePdfConfig
  } = useGetPdfMergeContent({
    enabled: config.enabled || false,
    filename: config.filename || "",
    options: Array.isArray(config.options) ? config.options : [],
  }, attachments, onConfigChange ?? (() => {
  }));

  const onValueChange_ = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    setMergePdfConfig({...mergePdfConfig, enabled: value});
    onConfigChange?.({enabled: value, filename: pdfFilename, options: mergePdfConfig.options});
  }

  const onMergePdfFilenameChange = (filename: string) => {
    setPdfFilename(filename);
    onConfigChange?.({enabled: config.enabled, filename: filename, options: mergePdfConfig.options});
  }

  const onUpdateMergePdfOption = (uid: string, updatedValues: Partial<MergePdfOption>) => {
    const newConfig = {
      enabled: config.enabled,
      filename: pdfFilename,
      options: mergePdfConfig.options.map((option: any) =>
        option.uid === uid ? {...option, ...updatedValues} : option
      )
    }
    setMergePdfConfig(newConfig);
  };

  const savePdfMergeOptions = () => {
    onConfigChange?.({enabled: config.enabled, filename: pdfFilename, options: mergePdfConfig.options});
    setIsPDFDialogOpen(false);
  };

  const isDisabled = disabled || (pdfMergeContent?.length < 2 || attachments?.length < 2);

  return (
    <div className="notebook-content-options">
      <FormGroup label="Merge PDF" className="content__email__merge-pdf">
        <Switch checked={mergePdfConfig.enabled} onChange={onValueChange_} disabled={isDisabled}>
          Combine separate PDF files into one
          <Button
            className="merge-pdf-filename"
            minimal={true}
            small={true}
            icon={IconNames.COG}
            disabled={disabled || !mergePdfConfig.enabled}
            onClick={() => setIsPDFDialogOpen(true)}
          />
        </Switch>
      </FormGroup>

      <Dialog
        isOpen={isPDFDialogOpen}
        onClose={() => setIsPDFDialogOpen(false)}
        title="PDF Merge Options"
        icon={IconNames.COG}
      >
        <div className="pdf-merge-dialog-content">
          <FilenameInput filename={pdfFilename} onChange={onMergePdfFilenameChange} type={'pdf'}/>

          <div className="pdf-merge-table-container" id="pdf_merge_infinite_scroll">
            <InfiniteScroll
              dataLength={mergePdfConfig.options?.length || 0}
              next={() => { /* not implemented yet */
              }}
              hasMore={false}
              loader={<div>Loading...</div>}
              scrollableTarget="pdf_merge_infinite_scroll"
            >
              <table className="pdf-merge-table">
                <thead>
                <tr className="pdf-merge-header">
                  <th>Content</th>
                  <th>Include</th>
                  <th>Position</th>
                </tr>
                </thead>
                <tbody>
                {config.enabled && pdfMergeContent.map((content: any, index: any) => (
                  <PDFMergeOptionsForm
                    key={content.uid}
                    uid={content.uid}
                    name={content.name}
                    position={index}
                    option={mergePdfConfig.options.find((opt: any) => opt.uid === content.uid)}
                    onUpdateMergePdfOption={onUpdateMergePdfOption}
                    positionLimit={pdfMergeContent.length}
                  />
                ))}
                </tbody>
              </table>
            </InfiniteScroll>
          </div>

          <div className="pdf-merge-dialog-footer">
            <Button onClick={() => setIsPDFDialogOpen(false)}>Cancel</Button>
            <Button intent="primary" onClick={savePdfMergeOptions}>
              Save
            </Button>
          </div>
        </div>
      </Dialog>
    </div>
  )
};

export default MergePdf;
